import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { ProfilelInfoFormInputs } from '../../../../interfaces/profile.interface';
import {
  useGetSpecialtiesByUniversityQuery,
  useGetUniversitiesQuery,
} from '../../../../store/apiSlice/universitySpecialtyApi';
import { useThemeContext } from '../../../../theme/ThemeContextProvider';
import ArrowDownIcon from '../../../Icons/ArrowDownIcon';
import AutocompleteOption from '../../../shared/AutocompleteOption/AutocompleteOption';
import { EducationLevelEnum } from '../../../../enums/education-type-enum';
import formatEnumValue from '../../../../utils/formatEnumValue';
import DateInput from '../../../MUIComponents/DateInput';

interface ProfileEducationFormProps {
  control: Control<ProfilelInfoFormInputs, any>;
  setValue: UseFormSetValue<ProfilelInfoFormInputs>;
  watch: UseFormWatch<ProfilelInfoFormInputs>;
  errors: FieldErrors<ProfilelInfoFormInputs>;
  index: number;
}

type EducationErrors = {
  level?: FieldError;
  universityName?: FieldError;
  specialtyName?: FieldError;
  startYear?: FieldError;
  endYear?: FieldError;
};

const ProfileEducationForm: React.FC<ProfileEducationFormProps> = ({
  control,
  setValue,
  watch,
  errors,
  index,
}) => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const educationErrors = errors?.educations?.[index] as
    | EducationErrors
    | undefined;

  // Track search inputs
  const [universitySearch, setUniversitySearch] = useState('');
  const [specialtySearch, setSpecialtySearch] = useState('');

  // Debounced search terms to avoid firing API requests on every keystroke
  const [debouncedUniversitySearch, setDebouncedUniversitySearch] =
    useState('');
  const [debouncedSpecialtySearch, setDebouncedSpecialtySearch] = useState('');

  const selectedLevel = watch(`educations.${index}.level`);
  const selectedUniversity = watch(`educations.${index}.universityName`);
  const selectedUniversityId = watch(`educations.${index}.idUniversity`);
  const selectedSpecialty = watch(`educations.${index}.specialtyName`);
  const selectedSpecialtyId = watch(`educations.${index}.idSpecialty`);

  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedUniversitySearch(universitySearch),
      300
    );
    return () => clearTimeout(handler);
  }, [universitySearch]);

  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedSpecialtySearch(specialtySearch),
      300
    );
    return () => clearTimeout(handler);
  }, [specialtySearch]);

  // Fetch universities based on debounced search input
  const { data: universitiesResponse, isLoading: isUniversitiesLoading } =
    useGetUniversitiesQuery(
      { page: 1, limit: 9000, search: debouncedUniversitySearch, sort: 'asc' },
      { refetchOnMountOrArgChange: true }
    );

  const universities = useMemo(
    () => universitiesResponse?.data || [],
    [universitiesResponse]
  );

  // Use simple string option lists for Autocomplete so that both selecting
  // from verified options and typing custom values behave predictably.
  const universityOptions = useMemo(
    () => universities.map((u) => u.name),
    [universities]
  );

  // Fetch specialties dynamically based on university ID and search
  const {
    data: specialtiesResponse,
    isLoading: isSpecialtiesLoading,
    refetch,
  } = useGetSpecialtiesByUniversityQuery(
    {
      universityId: selectedUniversityId || '',
      page: 1,
      limit: 9000,
      search: debouncedSpecialtySearch,
      sort: 'asc',
    },
    { skip: !selectedUniversityId }
  );

  const specialties = useMemo(
    () => (selectedUniversityId ? specialtiesResponse?.data || [] : []),
    [selectedUniversityId, specialtiesResponse]
  );

  const specialtyOptions = useMemo(
    () => specialties.map((s) => s.name),
    [specialties]
  );

  useEffect(() => {
    if (selectedUniversityId && universities.length) {
      const existingUniversity = universities.find(
        (uni) => uni.id === selectedUniversityId
      );
      if (existingUniversity) {
        setValue(`educations.${index}.universityName`, existingUniversity.name);
        setValue(`educations.${index}.idUniversity`, existingUniversity.id);
      }
    }

    if (selectedSpecialtyId && specialties.length) {
      const existingSpecialty = specialties.find(
        (spec) => spec.id === selectedSpecialtyId
      );
      if (existingSpecialty) {
        setValue(`educations.${index}.specialtyName`, existingSpecialty.name);
        setValue(`educations.${index}.idSpecialty`, existingSpecialty.id);
      }
    }
  }, [
    selectedUniversityId,
    selectedSpecialtyId,
    universities,
    specialties,
    setValue,
    index,
  ]);

  useEffect(() => {
    if (selectedUniversityId) {
      refetch();
    }
  }, [selectedUniversityId, refetch]);

  return (
    <Box
      mt="48px"
      display="flex"
      flexDirection="column"
      gap={mobileView ? '28px' : '36px'}
    >
      <Box
        display="flex"
        flexDirection={mobileView ? 'column' : 'row'}
        gap={mobileView ? '28px' : '22px'}
      >
        {/* Education Level Dropdown */}
        <Controller
          name={`educations.${index}.level`}
          control={control}
          render={() => (
            <FormControl fullWidth error={!!educationErrors?.level}>
              <Typography variant="caption" sx={{ mb: '8px' }}>
                Education level:
              </Typography>
              <Autocomplete
                options={Object.values(EducationLevelEnum)}
                getOptionLabel={(option) => formatEnumValue(option)}
                value={selectedLevel ?? undefined}
                onChange={(_, newValue) => {
                  setValue(`educations.${index}.level`, newValue ?? undefined, {
                    shouldDirty: true,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Choose one"
                    error={!!educationErrors?.level}
                    helperText={educationErrors?.level?.message || ''}
                  />
                )}
                renderOption={(props, option) => (
                  <AutocompleteOption
                    text={formatEnumValue(option)}
                    props={props}
                  />
                )}
                sx={{
                  '.MuiInputBase-root': { height: '50px' },
                  '.MuiSvgIcon-root': { bgcolor: 'transparent !important' },
                }}
                popupIcon={<ArrowDownIcon />}
              />
            </FormControl>
          )}
        />

        {/* University Select / Search */}
        <Controller
          name={`educations.${index}.universityName`}
          control={control}
          render={() => (
            <FormControl fullWidth error={!!educationErrors?.universityName}>
              <Typography variant="caption" sx={{ mb: '8px' }}>
                School:
              </Typography>
              <Autocomplete
                freeSolo
                options={universityOptions}
                loading={isUniversitiesLoading}
                value={selectedUniversity || ''}
                onInputChange={(_, newInputValue, reason) => {
                  // Ignore internal resets from MUI when the value is
                  // programmatically changed (e.g. after form reset).
                  if (reason === 'reset') {
                    return;
                  }

                  // Live-search universities as the user types
                  setUniversitySearch(newInputValue);

                  // Keep the form value in sync with what the user is typing
                  // so validation (required, regex, etc.) works while typing.
                  setValue(
                    `educations.${index}.universityName`,
                    newInputValue,
                    { shouldDirty: true, shouldValidate: true }
                  );

                  // While typing free text, treat it as a custom university
                  // with no id until it matches a known option via onChange.
                  setValue(`educations.${index}.idUniversity`, '', {
                    shouldDirty: true,
                  });
                }}
                onChange={(_, newValue) => {
                  const name = (newValue as string) || '';

                  // Persist the displayed university name
                  setValue(`educations.${index}.universityName`, name, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  // If the name matches a known university, store its id;
                  // otherwise, treat it as a custom entry with no id.
                  const found = universities.find((uni) => uni.name === name);
                  setValue(
                    `educations.${index}.idUniversity`,
                    found?.id || '',
                    { shouldDirty: true }
                  );

                  setUniversitySearch('');
                  setValue(`educations.${index}.specialtyName`, '', {
                    shouldDirty: true,
                  });
                  setValue(`educations.${index}.idSpecialty`, '', {
                    shouldDirty: true,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Choose or enter university"
                    error={!!educationErrors?.universityName}
                    helperText={educationErrors?.universityName?.message || ''}
                  />
                )}
                renderOption={(props, option) => (
                  <AutocompleteOption text={option} props={props} />
                )}
                sx={{
                  '.MuiInputBase-root': { height: '50px' },
                  '.MuiSvgIcon-root': { bgcolor: 'transparent !important' },
                }}
                popupIcon={<ArrowDownIcon />}
              />
            </FormControl>
          )}
        />
      </Box>

      <Box
        display="flex"
        flexDirection={mobileView ? 'column' : 'row'}
        gap={mobileView ? '28px' : '22px'}
      >
        {/* Specialty Select / Search */}
        <Controller
          name={`educations.${index}.specialtyName`}
          control={control}
          render={() => (
            <FormControl fullWidth error={!!educationErrors?.specialtyName}>
              <Typography variant="caption" sx={{ mb: '8px' }}>
                Field of study:
              </Typography>
              <Autocomplete
                freeSolo
                options={specialtyOptions}
                loading={isSpecialtiesLoading}
                value={selectedSpecialty || ''}
                onInputChange={(_: any, newInputValue: string, reason: any) => {
                  // Ignore internal resets from MUI when the value is
                  // programmatically changed (e.g. after form reset).
                  if (reason === 'reset') {
                    return;
                  }

                  // Live-search specialties as the user types
                  setSpecialtySearch(newInputValue);

                  // Keep the form value in sync so validation reflects what
                  // the user is typing.
                  setValue(`educations.${index}.specialtyName`, newInputValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  // While typing free text, clear idSpecialty until a known
                  // option is selected or matched.
                  setValue(`educations.${index}.idSpecialty`, '', {
                    shouldDirty: true,
                  });
                }}
                onChange={(_: any, newValue: string | null) => {
                  const name = newValue || '';

                  // Persist the displayed specialty name
                  setValue(`educations.${index}.specialtyName`, name, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  // If the name matches a known specialty, store its id;
                  // otherwise, treat it as a custom entry with no id.
                  const found = specialties.find((spec) => spec.name === name);
                  setValue(`educations.${index}.idSpecialty`, found?.id || '', {
                    shouldDirty: true,
                  });

                  setSpecialtySearch('');
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    placeholder="Enter your field of study"
                    error={!!educationErrors?.specialtyName}
                    helperText={educationErrors?.specialtyName?.message || ''}
                  />
                )}
                renderOption={(props: any, option: string) => (
                  <AutocompleteOption text={option} props={props} />
                )}
                sx={{
                  '.MuiInputBase-root': { height: '50px' },
                  '.MuiSvgIcon-root': { bgcolor: 'transparent !important' },
                }}
                popupIcon={<ArrowDownIcon />}
              />
            </FormControl>
          )}
        />
      </Box>

      {/* Data Pickers for mobile */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: mobileView ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: mobileView ? '0.5rem' : '22px',
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: '184px',
          }}
        >
          <DateInput<ProfilelInfoFormInputs>
            control={control}
            name={`educations.${index}.startYear`}
            label="Years:"
            placeholder={'Your start date'}
            error={educationErrors?.startYear}
            helperText={educationErrors?.startYear?.message || ''}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: '184px',
            marginTop: mobileView ? 0 : '15px',
          }}
        >
          <DateInput<ProfilelInfoFormInputs>
            control={control}
            name={`educations.${index}.endYear`}
            placeholder={'Your end date'}
            error={educationErrors?.endYear}
            helperText={educationErrors?.endYear?.message || ''}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileEducationForm;
